/**
 * Checkpoint Constants
 * Central location for all checkpoint-related constants
 */

// Conversation statuses that indicate the checkpoint has ended
export const ENDED_STATUSES = [
    "ended",
    "tutor_needed",
    "todo_activity_launch",
    "new_activity_generation",
]

// Checkpoint trigger contexts
export const CHECKPOINT_CONTEXTS = {
    STARTING_BLOCK: "starting_block",
    STARTING_BLOCK_STUDY_PROGRAM: "starting_block_study_program",
    RAISE_HAND: "raise_hand",
    SILENT_MODE_10MIN: "silent_mode_10min"
}

// Response action types
export const RESPONSE_ACTIONS = {
    PHOTO: "photo",
    ACTIVITY: "activity",
    TEXT: "text",
    TEXT_HOMEWORK: "text_homework",
    DATE: "date"
}

// Conversation machine states
export const CONVERSATION_STATES = {
    IDLE: "idle",
    LOADING: "loading",
    DISPLAYING: "displaying",
    RESPONDING: "responding",
    ENDED: "ended",
    ERROR: "error"
}

// Timing constants (in milliseconds)
export const TIMING = {
    CLOSE_BUTTON_DELAY: 10000,      // 10s before showing close button
    INITIAL_LOADING_DELAY: 1500,    // 1.5s initial loading animation
    FEEDBACK_CLOSE_DELAY: 1000,     // 1s delay after feedback before closing
    ANIMATION_DURATION: 300         // Standard animation duration
}

// Helper functions
export const isStartingBlockContext = (context) => {
    return context === CHECKPOINT_CONTEXTS.STARTING_BLOCK ||
           context === CHECKPOINT_CONTEXTS.STARTING_BLOCK_STUDY_PROGRAM
}

export const isEndedStatus = (status) => {
    return ENDED_STATUSES.includes(status)
}

// A message worth displaying: a non-empty string body. A conversation
// created or answered without an agent_message (AI failure, malformed
// response) fails this.
export const hasValidMessageBody = (message) => {
    return Boolean(message?.body && typeof message.body === "string" && message.body.trim().length > 0)
}

// A prerequisite quiz created with 'pending' status and no question yet
// (before the student starts it) — never shown, loading or not.
export const isQuizPendingWithoutQuestion = ({ message, isQuiz, quizStatus }) => {
    return Boolean(isQuiz) && quizStatus === "pending" && !hasValidMessageBody(message)
}

// True when a checkpoint conversation has nothing to show: no valid message
// once loading is done, or a prerequisite quiz still 'pending' with no
// question yet (checked regardless of loading — a quiz not started never had
// a loading state of its own to display). Left unhandled, the caller has
// nothing to render — which, inside SamCheckpointOverlay's fullscreen chrome,
// means an empty screen with no way out (see SamConversationBase's
// isEmptyContent usage). Takes the already-computed isQuizPendingWithoutQuestion
// rather than re-deriving it, since callers need that value on its own too
// (e.g. to label a Bugsnag report).
export const isEmptyCheckpointContent = ({ showLoading, message, isQuizNotStarted }) => {
    if (isQuizNotStarted) return true
    if (showLoading) return false
    return !hasValidMessageBody(message)
}
